import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import gmapInit from 'app/client/utils/map/gmapInit';
import { logError } from '@zg-rentals/log-error';
import { streetViewContainer, streetViewError, streetViewErrorBold } from './haloStreetView.styles';

type HaloStreetViewProps = {
  lat: number;
  lon: number;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

/**
 * HaloStreetView — Google Street View panorama for the HALO neighborhood spoke.
 *
 * Uses a 3-step approach to get front-facing street views:
 * 1. Geocode the full address → get ROOFTOP location
 * 2. Get driving directions to the address → get entrance location
 * 3. Create StreetViewPanorama at entrance, aimed at geocoded location
 *
 * This avoids "back alley" views by leveraging Directions API routing to building entrances.
 */
export const HaloStreetView = ({ lat, lon, address }: HaloStreetViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const streetViewRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const initializedRef = useRef(false);
  const [hasError, setHasError] = useState(false);

  const apiKey = useSelector((state: RootReduxState) => state.app.googleMaps?.key);
  const gmapApiReady = useSelector((state: RootReduxState) => state.app.gmapApiReady);

  const initialize = useCallback(() => {
    const container = containerRef.current;
    if (!container || !gmapApiReady || !window.google?.maps || initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    const geocoderService = new window.google.maps.Geocoder();
    const directionsService = new window.google.maps.DirectionsService();
    const streetViewService = new window.google.maps.StreetViewService();

    const getPanorama = (directionsLatLng: google.maps.LatLng, geocoderLatLng?: google.maps.LatLng) => {
      const headingLatLng = geocoderLatLng || directionsLatLng;

      streetViewService.getPanorama(
        { location: directionsLatLng, radius: 200, source: google.maps.StreetViewSource.OUTDOOR },
        (panoramaData: google.maps.StreetViewPanoramaData | null, status: google.maps.StreetViewStatus) => {
          if (status !== window.google.maps.StreetViewStatus.OK || !panoramaData?.location) {
            logError({ error: 'streetViewService.getPanorama', errorType: 'GoogleMapsError', context: { status } });
            setHasError(true);
            return;
          }

          const panoId = panoramaData.location.pano;
          const heading = window.google.maps.geometry.spherical.computeHeading(
            panoramaData.location.latLng!,
            headingLatLng,
          );

          streetViewRef.current = new window.google.maps.StreetViewPanorama(container, {
            pano: panoId,
            position: directionsLatLng,
            pov: { heading, pitch: 0 },
            addressControl: false,
            linksControl: false,
            visible: true,
            zoom: 0,
          });
        },
      );
    };

    const fullAddress = [address?.street, address?.city, address?.state, address?.zip].filter(Boolean).join(', ');

    if (!fullAddress) {
      const desiredLatLng = new window.google.maps.LatLng(lat, lon);
      getPanorama(desiredLatLng);
      return;
    }

    // Step 1: Geocode address
    geocoderService.geocode(
      { address: fullAddress },
      (results: google.maps.GeocoderResult[] | null, geocoderStatus: google.maps.GeocoderStatus) => {
        if (geocoderStatus !== window.google.maps.GeocoderStatus.OK || !results?.[0]) {
          logError({ error: 'geocoderService.geocode', errorType: 'GoogleMapsError', context: { geocoderStatus } });
          setHasError(true);
          return;
        }

        const geocoderLatLng = results[0].geometry.location;

        // Step 2: Get directions to find entrance location
        directionsService.route(
          {
            origin: fullAddress,
            destination: fullAddress,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (response: google.maps.DirectionsResult | null, directionsStatus: google.maps.DirectionsStatus) => {
            if (directionsStatus !== window.google.maps.DirectionsStatus.OK || !response?.routes?.[0]) {
              logError({
                error: 'directionsService.route',
                errorType: 'GoogleMapsError',
                context: { directionsStatus },
              });
              setHasError(true);
              return;
            }

            const directionsLatLng = response.routes[0].legs[0].start_location;

            // Step 3: Create panorama at entrance, aimed at geocoded location
            getPanorama(directionsLatLng, geocoderLatLng);
          },
        );
      },
    );
  }, [gmapApiReady, lat, lon, address]);

  useEffect(() => {
    if (apiKey) {
      gmapInit({ key: apiKey });
    }
  }, [apiKey]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    return () => {
      if (streetViewRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(streetViewRef.current);
        streetViewRef.current = null;
      }
      initializedRef.current = false;
    };
  }, []);

  if (hasError) {
    return (
      <div className={streetViewError}>
        <div className={streetViewErrorBold}>Uh oh.</div>
        Sorry, street view is not available for this property.
      </div>
    );
  }

  return <div ref={containerRef} className={streetViewContainer} data-testid="halo-street-view" />;
};
